import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Stack,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Email as EmailIcon,
  Security as SecurityIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import {
  PrimaryTextField,
  ErrorMessage,
  Label,
  PrimaryLoadingButton,
  PrimaryLiteButton,
} from 'components';
import {
  selectMfaLoader,
  selectMfaChallengeId,
  selectMfaError,
  selectMfaOriginalCredentials,
} from 'base/mfa/selectors';
import { IMfaVerificationForm } from 'base/mfa/interface';
import { mfaSliceActions } from 'base/mfa/slice';

interface Props {
  mfaType: 'EMAIL' | 'TOTP';
  onBack: () => void;
  onCancel: () => void;
}

const validationSchema = yup.object().shape({
  code: yup
    .string()
    .required('Verification code is required')
    .min(6, 'Code must be at least 6 characters')
    .max(16, 'Code must be at most 16 characters'),
  isUsingBackupCode: yup.boolean(),
});

const MfaVerificationView: React.FC<Props> = ({ mfaType, onBack, onCancel }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [challengeInitiated, setChallengeInitiated] = useState(false);

  const challengeId = useSelector(selectMfaChallengeId);
  const loading = useSelector(selectMfaLoader);
  const mfaError = useSelector(selectMfaError);
  const originalCredentials = useSelector(selectMfaOriginalCredentials);

  const {
    control,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<IMfaVerificationForm>({
    defaultValues: {
      mfaType,
      code: '',
      isUsingBackupCode: false,
    },
    resolver: yupResolver(validationSchema),
  });

  const isUsingBackupCode = watch('isUsingBackupCode');

  useEffect(() => {
    reset({
      mfaType,
      code: '',
      isUsingBackupCode: false,
    });
    setChallengeInitiated(false);
    dispatch(mfaSliceActions.resetMfaChallengeState());
  }, [mfaType, reset, dispatch]);

  useEffect(() => () => {
    dispatch(mfaSliceActions.resetMfaChallengeState());
  }, [dispatch]);

  useEffect(() => {
    if (mfaError) {
      setError('code', { type: 'manual', message: mfaError });
    } else {
      clearErrors('code');
    }
  }, [mfaError, setError, clearErrors]);

  useEffect(() => {
    if (!originalCredentials) {
      dispatch(mfaSliceActions.verifyMfaFailed('Credentials not found. Please restart the sign-in process.'));
      return;
    }

    if (!challengeInitiated && !challengeId && !loading) {
      dispatch(mfaSliceActions.initiateMfaChallenge({ mfaType }));
      setChallengeInitiated(true);
    }
  }, [dispatch, mfaType, challengeInitiated, challengeId, loading, originalCredentials]);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (challengeInitiated && !challengeId && !loading) {
      timer = setTimeout(() => {
        dispatch(mfaSliceActions.verifyMfaFailed('Failed to initiate MFA challenge. Please try again.'));
      }, 5000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [challengeInitiated, challengeId, loading, dispatch]);

  const getMfaTypeIcon = (type: 'EMAIL' | 'TOTP') => (
    type === 'EMAIL' ? <EmailIcon /> : <SecurityIcon />
  );

  const getCodePlaceholder = () => {
    if (isUsingBackupCode) {
      return 'Enter backup code';
    }

    return mfaType === 'EMAIL' ? 'Enter email code' : 'Enter authenticator code';
  };

  const onSubmit = async (data: IMfaVerificationForm) => {
    if (!challengeId) {
      dispatch(mfaSliceActions.verifyMfaFailed('Challenge not initiated. Please restart the process.'));
      return;
    }

    setIsSubmitting(true);
    try {
      dispatch(mfaSliceActions.verifyMfa({ ...data, challengeId }));
    } catch (err) {
      dispatch(mfaSliceActions.verifyMfaFailed('MFA verification failed. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    // Reset challenge state while preserving original credentials when going back
    dispatch(mfaSliceActions.resetMfaChallengeState());
    setChallengeInitiated(false);
    onBack();
  };

  const handleCancel = () => {
    // Reset challenge state while preserving original credentials when canceling
    dispatch(mfaSliceActions.resetMfaChallengeState());
    setChallengeInitiated(false);
    onCancel();
  };

  return (
    <Box sx={{ width: '100%' }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 2,
              color: '#9d8336',
              '& svg': { fontSize: 48 },
            }}
          >
            {getMfaTypeIcon(mfaType)}
          </Box>

          <Box>
            <Controller
              control={control}
              name="code"
              render={({ field }) => (
                <PrimaryTextField
                  {...field}
                  label="Verification Code"
                  placeholder={getCodePlaceholder()}
                  fullWidth
                  autoComplete="one-time-code"
                  error={!!errors?.code}
                  inputProps={{
                    maxLength: isUsingBackupCode ? 16 : 8,
                    style: {
                      textAlign: 'center',
                      fontSize: '1.2rem',
                      letterSpacing: '0.1em',
                    },
                  }}
                />
              )}
            />
            <ErrorMessage>{errors?.code?.message}</ErrorMessage>
          </Box>

          {/* Backup Code Option */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Controller
              control={control}
              name="isUsingBackupCode"
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} size="small" />}
                  label={<Label sx={{ fontSize: '14px' }}>Use backup code instead</Label>}
                />
              )}
            />
          </Box>

          {/* Loading State */}
          {loading && !challengeId && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Label sx={{ fontSize: '14px', color: '#666' }}>
                Initiating MFA challenge...
              </Label>
            </Box>
          )}

          {/* Info Alert */}
          {mfaType === 'EMAIL' && !isUsingBackupCode && challengeId && (
            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
              Didn&apos;t receive the email? Check your spam folder or try again in a few minutes.
            </Alert>
          )}

          {mfaType === 'EMAIL' && isUsingBackupCode && (
            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
              {'Need a backup code to finish email verification? Backup codes are issued only by your organisation\'s platform administrators. Contact them directly to obtain a code and regain access securely.'}
            </Alert>
          )}

          {/* Actions - Using consistent button styling */}
          <Stack spacing={2}>
            <PrimaryLoadingButton
              type="submit"
              fullWidth
              size="large"
              loading={isSubmitting}
              disabled={!challengeId || loading}
              sx={{ py: 1.5 }}
            >
              {isSubmitting ? 'Verifying...' : 'Verify & Continue'}
            </PrimaryLoadingButton>

            <Stack direction="row" spacing={2} justifyContent="space-between">
              <PrimaryLiteButton
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{ minWidth: 100 }}
              >
                Back
              </PrimaryLiteButton>

              <PrimaryLiteButton
                onClick={handleCancel}
                sx={{ minWidth: 100 }}
              >
                Cancel
              </PrimaryLiteButton>
            </Stack>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
};

export default MfaVerificationView;
